import { IStyle, Stack } from '@fluentui/react';
import { Loader, useClassNames } from '@h2oai/ui-kit';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { stackStylesPage } from '../../themes/themes';
import { ClassNamesFromIStyles } from '../../utils/models';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { useGranularMLOpsPermissions } from './userActions';

interface INavigationWrapperStyles {
  container: IStyle;
  stack: IStyle;
  noPermissions: IStyle;
}

const navigationWrapperStyles = (): Partial<INavigationWrapperStyles> => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
  },
  noPermissions: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'pre-line',
    textAlign: 'center',
  },
});

const NavigationWrapper: React.FC = ({ children }) => {
  const { pathname } = useLocation(),
    classNames = useClassNames<INavigationWrapperStyles, ClassNamesFromIStyles<INavigationWrapperStyles>>(
      'navigationWrapper',
      navigationWrapperStyles
    ),
    { ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized } = useWorkspaces(),
    permissions = useGranularMLOpsPermissions(ACTIVE_WORKSPACE_NAME);

  const loading = Boolean(!activeWorkspaceInitialized || permissions.loading);
  const [showLoader, setShowLoader] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (loading) {
      const timerId = window.setTimeout(() => setShowLoader(true), 1000);
      return () => window.clearTimeout(timerId);
    } else {
      setShowLoader(false);
      return undefined;
    }
  }, [loading]);

  const restrictAccess = React.useMemo(() => {
    if (loading) return true;
    const pathsPermissions = [
      {
        path: '/mlops/workspaces/[id]',
        regexp: /^\/mlops\/workspaces\/[^/]+$/,
        permission: permissions?.hasAccessToDashboardPage,
      },
      {
        path: 'models',
        regexp: /^\/mlops\/workspaces\/[^/]+\/models$/,
        permission: permissions?.hasAccessToModelsPage,
      },
      {
        path: 'models/add-new',
        regexp: /^\/mlops\/workspaces\/[^/]+\/models\/add-new$/,
        permission: permissions?.hasAccessToModelDetailPage_create,
      },
      {
        path: 'models/[id]',
        regexp: /^\/mlops\/workspaces\/[^/]+\/models\/(?!add-new$|manage-tags$)[^/]+$/,
        permission: permissions?.hasAccessToModelDetailPage_view,
      },
      {
        path: 'models/manage-tags',
        regexp: /^\/mlops\/workspaces\/[^/]+\/models\/manage-tags$/,
        permission: permissions?.hasAccessToTagsPage,
      },
      {
        path: 'deployments',
        regexp: /^\/mlops\/workspaces\/[^/]+\/deployments$/,
        permission: permissions?.hasAccessToDeploymentsPage,
      },
      {
        path: 'deployments/create-new',
        regexp: /^\/mlops\/workspaces\/[^/]+\/deployments\/create-new$/,
        permission: permissions?.hasAccessToDeploymentDetailPage_create,
      },
      {
        path: 'deployments/[id]',
        regexp: /^\/mlops\/workspaces\/[^/]+\/deployments\/(?!create-new$)[^/]+$/,
        permission: permissions?.hasAccessToDeploymentDetailPage_view,
      },
      {
        path: 'jobs',
        regexp: /^\/mlops\/workspaces\/[^/]+\/jobs$/,
        permission: permissions?.hasAccessToBatchScoringPage,
      },
      {
        path: 'jobs/start-new',
        regexp: /^\/mlops\/workspaces\/[^/]+\/jobs\/start-new$/,
        permission: permissions?.hasAccessToBatchScoringDetailPage_create,
      },
      {
        path: 'jobs/[id]',
        regexp: /^\/mlops\/workspaces\/[^/]+\/jobs\/(?!start-new$)[^/]+$/,
        permission: permissions?.hasAccessToBatchScoringDetailPage_view,
      },
    ];
    return pathsPermissions.some(({ regexp, permission }) => regexp.test(pathname) && !permission);
  }, [pathname, permissions, loading]);

  return (
    <div className={classNames.container}>
      <Stack styles={stackStylesPage} className={classNames.stack}>
        {restrictAccess ? (
          <div className={classNames.noPermissions}>
            {loading ? (
              showLoader ? (
                <Loader label="Just a moment, checking your permissions..." />
              ) : null
            ) : (
              `You have no access to this page.\nAsk the workspace owner to grant you permissions.`
            )}
          </div>
        ) : (
          children
        )}
      </Stack>
    </div>
  );
};

export default NavigationWrapper;
